import { PlusIcon } from '@phosphor-icons/react';
import { Formik } from 'components/form/formik';
import diff from 'object-diff';
import { useMemo } from 'react';
import toast from 'react-hot-toast';
import { COUNTRY_VALUES } from '@utils/address';
import * as Yup from 'yup';

import { Button } from 'components/button/Button';
import { Card } from 'components/Card';
import { InputField } from 'components/input/InputField';
import { SimpleSelectField } from 'components/select/SimpleSelectField';
import { Pill } from '../../../components/pill/Pill';
import { LANGUAGE_VALUES } from '../../../utils/language';
import { formatVatNumber } from '../../../utils/vat-number';
import { getDisplayError } from '../../../utils/get-display-error';
import { useCarrierPortalSupplier } from '../CarrierPortalCarrierContext';
import { AutocompletePostalcode } from '../../location/components/AutocompletePostalcode';
import { useTranslation } from '../../../contexts/translation-context';
import { DataField } from '../../../components/DataField';
import { linkCarrierPortalSupplierDocument, updateCarrierPortalCarrier } from '../models';
import { captureException } from '@sentry/react';
import { DocumentStatusRow } from './DocumentStatusRow';

const updateCustomerSchema = Yup.object().shape({
  street: Yup.string().min(1, 'errors.required').required('errors.required'),
  streetNumber: Yup.string(),
  city: Yup.string().min(1, 'errors.required').required('errors.required'),
  postalCode: Yup.string().min(1, 'errors.required').required('errors.required'),
  country: Yup.mixed().nullable().required('errors.required'),
  language: Yup.mixed().nullable().required('errors.required'),
});

export const CarrierPortalInfoPage = () => {
  const { supplier, carrierId, refreshData } = useCarrierPortalSupplier();
  const { i18n } = useTranslation();

  const initialValues = useMemo(() => {
    return {
      street: supplier.street,
      streetNumber: supplier.streetNumber ?? '',
      city: supplier.city,
      postalCode: supplier.postalCode,
      country: COUNTRY_VALUES.find((v) => v.key === supplier.country)!,
      language: LANGUAGE_VALUES.find((v) => v.key === supplier.language)!,
    };
  }, [supplier]);

  const insurances = (
    [
      ['CivilLiabilityInsurance', 'carrierPortal.businessInformation.civilLiabilityInsurance'],
      ['CmrInsurance', 'carrierPortal.businessInformation.CMRInsurance'],
      ['TransportInsurance', 'carrierPortal.businessInformation.transportationInsurance'],
    ] as const
  ).map(([type, labelKey]) => {
    const file = supplier.documents.find((d) => d.type === type)?.document ?? null;
    return { type, label: i18n(labelKey), file };
  });

  const uploadedCount = insurances.filter((d) => d.file).length;
  const missingCount = insurances.length - uploadedCount;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-2.5">
        <h1 className="heading-one">{supplier.name}</h1>
        {missingCount > 0 && (
          <Pill color="warning">
            {`${i18n('carrierPortal.businessInformation.insurances')} · ${i18n(
              'carrierPortal.trucks.statusMissing',
            )}${missingCount > 1 ? ` (${missingCount})` : ''}`}
          </Pill>
        )}
      </div>

      <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,1fr)_380px]">
        <div className="flex flex-col gap-2.5">
          <Card title={i18n('customerPortal.businessInformation.businessInformation')}>
            <div className="flex flex-col gap-4">
              <DataField title={i18n('customerPortal.businessInformation.name')}>{supplier.name}</DataField>

              <div className="grid grid-cols-2 gap-4">
                <DataField title={i18n('customerPortal.businessInformation.vatNumber')}>
                  {supplier.vatNumber ? formatVatNumber(supplier.vatNumber) : '-'}
                </DataField>
                <DataField title={i18n('customerPortal.businessInformation.businessNumber')}>
                  {supplier.companyNumber ? formatVatNumber(supplier.companyNumber) : '-'}
                </DataField>
              </div>

              <div className="h-px w-full bg-border" />

              <Formik
                initialValues={initialValues}
                validationSchema={updateCustomerSchema}
                onSubmit={async (newValues) => {
                  try {
                    const patch: Partial<typeof newValues> = diff(initialValues, newValues);
                    const updateData: {
                      street?: string;
                      streetNumber?: string;
                      city?: string;
                      postalCode?: string;
                      country?: string;
                      language?: string;
                    } = {};

                    if (patch.street !== undefined) updateData.street = patch.street;
                    if (patch.streetNumber !== undefined) updateData.streetNumber = patch.streetNumber;
                    if (patch.city !== undefined) updateData.city = patch.city;
                    if (patch.postalCode !== undefined) updateData.postalCode = patch.postalCode;
                    if (patch.country) updateData.country = patch.country.key;
                    if (patch.language) updateData.language = patch.language.key;

                    if (Object.keys(updateData).length) {
                      await updateCarrierPortalCarrier(carrierId, updateData);
                    }
                    toast.success(i18n('customerPortal.businessInformation.informationChanged'));
                  } catch (err: any) {
                    captureException(err);
                    toast.error(
                      i18n('customerPortal.businessInformation.informationNotChanged', {
                        error: getDisplayError(err),
                      }),
                    );
                  }
                }}
              >
                {({ handleSubmit, isSubmitting }) => {
                  return (
                    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                      <div className="flex gap-4">
                        <div className="flex-4">
                          <InputField labelText={i18n('customerPortal.locations.street')} type="text" name="street" />
                        </div>
                        <div className="flex-1">
                          <InputField
                            labelText={i18n('customerPortal.locations.number')}
                            type="text"
                            name="streetNumber"
                          />
                        </div>
                      </div>
                      <AutocompletePostalcode />
                      <div className="max-w-[320px]">
                        <SimpleSelectField
                          labelText={i18n('customerPortal.locations.language')}
                          items={LANGUAGE_VALUES}
                          name="language"
                        />
                      </div>

                      <div>
                        <Button
                          type="submit"
                          color="primary"
                          isDisabled={isSubmitting}
                          isLoading={isSubmitting}
                          iconLeft={<PlusIcon className="button-icon" />}
                        >
                          {i18n('customerPortal.businessInformation.saveChanges')}
                        </Button>
                      </div>
                    </form>
                  );
                }}
              </Formik>
            </div>
          </Card>

          <div className="text-2xs text-fg-muted">{i18n('customerPortal.businessInformation.requestChanges')}</div>
        </div>

        <Card
          title={i18n('carrierPortal.businessInformation.insurances')}
          rightHeading={
            <span className="card-subtle-text">
              {i18n('carrierPortal.trucks.documentCount', {
                count: `${uploadedCount}`,
                total: `${insurances.length}`,
              })}
            </span>
          }
          flush
        >
          {insurances.map((insurance) => (
            <DocumentStatusRow
              key={insurance.type}
              label={insurance.label}
              /* Supplier insurances carry no stored expiry date, so presence is all we can report. */
              status={insurance.file ? 'valid' : 'missing'}
              file={insurance.file}
              uploadName={`${insurance.label} - ${supplier.id}`}
              onUpload={async (document) => {
                await linkCarrierPortalSupplierDocument(carrierId, document.id, insurance.type);
                refreshData();
                toast.success(i18n('carrierPortal.businessInformation.documentUploaded'));
              }}
            />
          ))}
        </Card>
      </div>
    </div>
  );
};
